import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Box, Card, CardContent, Divider, LinearProgress, IconButton as MIconButton, Stack, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { permissionLevelEnum } from '../../../../constants';
import { useMachinesQuery } from '../../../../hooks/queries/useMachinesQuery';
import { useToolsQuery } from '../../../../hooks/queries/useToolsQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { Machine } from '../../../../services/apiSLTP/materials/machines';
import { Tool } from '../../../../services/apiSLTP/materials/tools';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import ModalBody, { ModalProps } from '../../ModalBody';
import CreateDailyMachineModal from './ManageDailyMachineModal';
import CreateDailyToolModal from './ManageDailyToolModal';

interface RemainingMaterialModalProps extends ModalProps {
    open: boolean;
    handleClose: () => void;
    activities: any[];
    initDate: string;
    refetch: () => void;
    currentDate: Date;
    siteId?: string;
}

const RemainingMaterialModal: React.FC<RemainingMaterialModalProps> = ({ open, handleClose, activities, refetch }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
    const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
    const [showCreateDailyMachineModal, setShowCreateDailyMachineModal] = useState(false);
    const [showCreateDailyToolModal, setShowCreateDailyToolModal] = useState(false);

    const { data: dataMachines, isLoading: isMachinesLoading } = useMachinesQuery({ queries: { active: true } });
    const { data: dataTools, isLoading: isToolsLoading } = useToolsQuery({ queries: { active: true } });

    const activitiesMachines = activities.flatMap((a) => a.dailyMachines.map((m: any) => m.machineId._id));
    const activitiesTools = activities.flatMap((a) => a.dailyTools.map((t: any) => t.toolId._id));

    const groupedMachines = useMemo(() => {
        if (!dataMachines) return [];

        const remaining = dataMachines.filter((m) => !activitiesMachines.includes(m._id));

        const groups = remaining.reduce((acc: Record<string, { type: any; items: Machine[] }>, machine) => {
            const type = machine.machineTypeId || { designation: t('unknownType'), displayOrder: Infinity };
            const key = type._id || type.designation;
            if (!acc[key]) acc[key] = { type, items: [] };
            acc[key].items.push(machine);
            return acc;
        }, {});

        return Object.values(groups)
            .sort((a, b) => {
                const orderA = a.type.displayOrder ?? Infinity;
                const orderB = b.type.displayOrder ?? Infinity;
                if (orderA !== orderB) return orderA - orderB;
                return a.type.designation.localeCompare(b.type.designation);
            })
            .map((g) => ({
                ...g,
                items: g.items.sort((x, y) => x.designation.localeCompare(y.designation)),
            }));
    }, [dataMachines, activitiesMachines, t]);

    const groupedTools = useMemo(() => {
        if (!dataTools) return [];

        const remaining = dataTools.filter((t) => !activitiesTools.includes(t._id));

        const groups = remaining.reduce((acc: Record<string, { type: any; items: Tool[] }>, tool) => {
            const type = tool.toolTypeId || { designation: t('unknownType'), displayOrder: Infinity };
            const key = type._id || type.designation;
            if (!acc[key]) acc[key] = { type, items: [] };
            acc[key].items.push(tool);
            return acc;
        }, {});

        return Object.values(groups)
            .sort((a, b) => {
                const orderA = a.type.displayOrder ?? Infinity;
                const orderB = b.type.displayOrder ?? Infinity;
                if (orderA !== orderB) return orderA - orderB;
                return a.type.designation.localeCompare(b.type.designation);
            })
            .map((g) => ({
                ...g,
                items: g.items.sort((x, y) => x.designation.localeCompare(y.designation)),
            }));
    }, [dataTools, activitiesTools, t]);

    const canAdd = user && user.profileId.permsLevel <= permissionLevelEnum.PLANNING;

    return (
        <ModalBody open={open} handleClose={handleClose} title={t('remainingMaterial')} showSubmitButton={false}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* MACHINES */}
                <Typography variant="h6" sx={{}}>
                    {capitalizeFirstLetter(t('machines'))}
                </Typography>
                {isMachinesLoading ? (
                    <LinearProgress />
                ) : groupedMachines.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {capitalizeFirstLetter(t('noRemainingMachine'))}
                    </Typography>
                ) : (
                    groupedMachines.map(({ type, items }) => (
                        <Card key={type._id || type.designation} variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    {type.designation}
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                <Stack>
                                    {items.map((machine) => (
                                        <Stack
                                            key={machine._id}
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            sx={{
                                                p: 1,
                                                borderRadius: 1,
                                                bgcolor: 'background.default',
                                                '&:hover': { bgcolor: 'action.hover' },
                                            }}
                                        >
                                            <Typography>{machine.designation}</Typography>
                                            {canAdd && (
                                                <MIconButton
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedMachine(machine);
                                                        setShowCreateDailyMachineModal(true);
                                                    }}
                                                >
                                                    <AddCircleIcon />
                                                </MIconButton>
                                            )}
                                        </Stack>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    ))
                )}

                {/* TOOLS */}
                <Typography variant="h6" sx={{}}>
                    {capitalizeFirstLetter(t('tools'))}
                </Typography>
                {isToolsLoading ? (
                    <LinearProgress />
                ) : groupedTools.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {capitalizeFirstLetter(t('noRemainingTool'))}
                    </Typography>
                ) : (
                    groupedTools.map(({ type, items }) => (
                        <Card key={type._id || type.designation} variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    {type.designation}
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                <Stack>
                                    {items.map((tool) => (
                                        <Stack
                                            key={tool._id}
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            sx={{
                                                p: 1,
                                                borderRadius: 1,
                                                bgcolor: 'background.default',
                                                '&:hover': { bgcolor: 'action.hover' },
                                            }}
                                        >
                                            <Typography>{tool.designation}</Typography>
                                            {canAdd && (
                                                <MIconButton
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedTool(tool);
                                                        setShowCreateDailyToolModal(true);
                                                    }}
                                                >
                                                    <AddCircleIcon />
                                                </MIconButton>
                                            )}
                                        </Stack>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    ))
                )}
            </Box>

            {/* Modals création */}
            {showCreateDailyMachineModal && (
                <CreateDailyMachineModal
                    open={showCreateDailyMachineModal}
                    activityId=""
                    handleClose={() => setShowCreateDailyMachineModal(false)}
                    refetch={refetch}
                    dailyMachine={null}
                    activities={activities}
                    canChooseActivity
                    machine={selectedMachine}
                    createTransfer
                />
            )}
            {showCreateDailyToolModal && (
                <CreateDailyToolModal
                    open={showCreateDailyToolModal}
                    activityId=""
                    handleClose={() => setShowCreateDailyToolModal(false)}
                    refetch={refetch}
                    dailyTool={null}
                    activities={activities}
                    canChooseActivity
                    tool={selectedTool}
                    createTransfer
                />
            )}
        </ModalBody>
    );
};

export default RemainingMaterialModal;
